/* Lemon Park — Peel & Stick landing page behaviour.
   One deferred asset for every lp-* section, so the sections themselves stay
   pure markup and re-initialise cleanly in the theme editor.

   The hero broadcasts the chosen pattern; the final CTA (and the
   before/after wall, when it opts in) repaper themselves from it. Subscribers that
   initialise *after* the hero has already picked (the hero sits above them in
   the DOM) get the current pattern replayed, so the first selection is never
   lost.

   ?product={handle} on the peel-stick page loads that product via the JSON
   endpoint and switches to single-product mode: picker hidden, pattern and
   lifestyle images pulled from the product gallery. */
(function () {
  'use strict';

  /* The tag may appear more than once (layout + reused sections), and an
     identical src is fetched once but executed per tag. Run the setup once. */
  if (window.__lpSectionsLoaded) return;
  window.__lpSectionsLoaded = true;

  var EVENT = 'lp:pattern';

  /* ------------------------------------------------------------ pattern bus */

  var bus = (window.LPPattern = window.LPPattern || {
    current: null,
    publish: function (pattern) {
      this.current = pattern;
      document.dispatchEvent(new CustomEvent(EVENT, { detail: pattern }));
    },
    subscribe: function (fn) {
      document.addEventListener(EVENT, function (event) {
        fn(event.detail);
      });
      if (this.current) fn(this.current);
    }
  });

  /* Marks a root as initialised so repeat init passes are no-ops. */
  function claim(root, key) {
    if (root.dataset[key] === 'ready') return false;
    root.dataset[key] = 'ready';
    return true;
  }

  /* -------------------------------------------------------- product loader */

  function getProductHandle() {
    var match = location.search.match(/[?&]product=([^&]+)/);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1].replace(/\+/g, ' ')).trim();
    } catch (error) {
      return null;
    }
  }

  function cdnImageUrl(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + width;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* "{product}" in merchant copy becomes the product name. `html` keeps the
     template's own markup (an <em> in a heading) and escapes only the name. */
  function fillTemplate(template, name, html) {
    var value = html ? escapeHtml(name) : name;
    return String(template || '').replace(/\{\{?\s*product\s*\}\}?/gi, value);
  }

  function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* Product name as shown in copy: the title minus the hero's "Trim from
     product title" setting ("Hedgerow Wallpaper" -> "Hedgerow"). */
  function productName(product, remove) {
    var name = product.title || '';
    if (remove) {
      var trimmed = name
        .replace(new RegExp('\\s*' + escapeRegExp(remove) + '\\s*', 'i'), ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (trimmed) name = trimmed;
    }
    return name;
  }

  /* Gallery roles in single-product mode: image 1 is the swatch, image 2
     the hero room photo, the rest feed the social-proof photos. */
  function heroConfig() {
    var hero = document.querySelector('[data-lp-hero]');
    var data = hero ? hero.dataset : {};
    return {
      remove: data.titleRemove !== undefined ? data.titleRemove : 'Wallpaper',
      ctaLabel: data.ctaLabel || 'Shop',
      eyebrow: data.productEyebrow || '',
      heading: data.productHeading || ''
    };
  }

  function buildPattern(product) {
    var images = product.images || [];
    if (!images[0]) return null;

    var variant = (product.variants || [])[0];

    return {
      img: cdnImageUrl(images[0], 1024),
      name: productName(product, heroConfig().remove),
      url: product.url || '/products/' + product.handle,
      handle: product.handle || '',
      variantId: variant && variant.id ? String(variant.id) : ''
    };
  }

  /* Points the room visualizer app block(s) inside the hero at a pattern, so
     "See it in your room" renders the print on show. The widget re-reads
     these attributes each time it opens (visualize-it.js, Widget.open). */
  function retargetVisualizer(root, pattern) {
    var handle = pattern.handle || (pattern.url || '').replace(/^.*\/products\//, '').split(/[?#]/)[0];
    if (!handle) return;

    Array.prototype.forEach.call(root.querySelectorAll('[data-pp-visualize]'), function (node) {
      node.setAttribute('data-product-handle', handle);
      node.setAttribute('data-product-title', pattern.name || '');
      if (pattern.variantId) {
        node.setAttribute('data-variant-id', pattern.variantId);
      } else {
        node.removeAttribute('data-variant-id');
      }
    });
  }

  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js').then(function (response) {
      if (!response.ok) throw new Error('product not found');
      return response.json();
    });
  }

  function setLink(node, url) {
    if (!node) return;
    if (url) {
      node.href = url;
      node.removeAttribute('aria-disabled');
    } else {
      node.removeAttribute('href');
      node.setAttribute('aria-disabled', 'true');
    }
  }

  function applyLifestylePhoto(img, src, alt) {
    img.src = cdnImageUrl(src, 700);
    img.srcset =
      cdnImageUrl(src, 350) +
      ' 350w, ' +
      cdnImageUrl(src, 700) +
      ' 700w, ' +
      cdnImageUrl(src, 1000) +
      ' 1000w';
    img.removeAttribute('width');
    img.removeAttribute('height');
    img.alt = alt;
    img.hidden = false;
  }

  /* Fills the two photo slots from the product's extra gallery images
     (beyond the swatch and hero photo). A slot the product cannot fill keeps
     the fallback photo set in the theme editor, or hides if there is none. */
  function applySocialProof(product) {
    var proof = document.querySelector('[data-lp-proof]');
    if (!proof) return;

    var extras = (product.images || []).slice(2);
    var photos = proof.querySelector('[data-lp-proof-photos]');
    var slots = [
      proof.querySelector('[data-lp-proof-photo="1"]'),
      proof.querySelector('[data-lp-proof-photo="2"]')
    ].filter(Boolean);
    var alt = (product.title || 'This pattern') + ' peel & stick wallpaper in a room';

    slots.forEach(function (img, i) {
      if (extras[i]) {
        applyLifestylePhoto(img, extras[i], alt);
      } else {
        img.hidden = !img.getAttribute('src');
      }
    });

    var visible = slots.filter(function (img) {
      return !img.hidden;
    });

    /* The first of two photos sits lower, per the design. */
    visible.forEach(function (img, i) {
      img.classList.toggle('lp-proof__img--offset', i === 0 && visible.length > 1);
    });

    if (photos) photos.hidden = visible.length === 0;
    proof.classList.toggle('lp-proof--no-photos', visible.length === 0);
    proof.classList.toggle('lp-proof--single', visible.length === 1);
  }

  function applyFinalCta(pattern) {
    var ctaRoot = document.querySelector('[data-lp-cta]');
    if (!ctaRoot) return;

    var data = ctaRoot.dataset;
    var heading = ctaRoot.querySelector('[data-lp-cta-heading]');
    var body = ctaRoot.querySelector('[data-lp-cta-body]');
    var primary = ctaRoot.querySelector('[data-lp-cta-primary]');
    var secondary = ctaRoot.querySelector('[data-lp-cta-secondary]');

    if (heading && data.productHeading) heading.innerHTML = fillTemplate(data.productHeading, pattern.name, true);
    if (body && data.productBody) body.textContent = fillTemplate(data.productBody, pattern.name);

    if (primary) {
      primary.textContent = fillTemplate(data.productPrimary || 'Shop {product}', pattern.name);
      setLink(primary, pattern.url);
    }

    if (secondary) {
      if (data.productSecondary) secondary.textContent = fillTemplate(data.productSecondary, pattern.name);
      setLink(secondary, pattern.url);
    }
  }

  /* Papers the whole hero with the product's swatch. The tile layer is a
     direct child of the hero root; the injected div must keep .no-empty or
     the theme's div:empty rule hides it. */
  function applyHeroTile(root, tileUrl) {
    if (!tileUrl) return;

    var tiles = root.querySelector('.lp-hero__tiles');
    if (tiles) {
      tiles.innerHTML =
        '<div class="lp-hero__tile lp-hero__tile--active no-empty" style="background-image:url(\'' +
        tileUrl +
        '\')"></div>';
      return;
    }

    root.style.backgroundImage = "url('" + tileUrl + "')";
    root.style.backgroundRepeat = 'repeat';
    root.style.backgroundSize = 'var(--lp-tile-size, 240px)';
  }

  function applyHeroProduct(root, pattern, product) {
    var images = product.images || [];
    var heroPhoto = images[1] || images[0];
    var picker = root.querySelector('.lp-hero__picker');
    var browse = root.querySelector('.lp-hero__browse');
    var scenes = root.querySelector('.lp-hero__scenes');
    var ctaSet = root.querySelector('.lp-hero__cta-set');
    var nameSet = root.querySelector('.lp-hero__name-set');
    var eyebrow = root.querySelector('[data-lp-hero-eyebrow]');
    var heading = root.querySelector('[data-lp-hero-heading]');
    var data = root.dataset;

    root.classList.add('lp-hero--product-mode');
    retargetVisualizer(root, pattern);

    if (eyebrow && data.productEyebrow) eyebrow.textContent = fillTemplate(data.productEyebrow, pattern.name);
    if (heading && data.productHeading) heading.innerHTML = fillTemplate(data.productHeading, pattern.name, true);

    applyHeroTile(root, pattern.img);

    if (picker) {
      picker.hidden = true;
      picker.style.display = 'none';
    }

    if (browse) browse.hidden = true;

    if (scenes && heroPhoto) {
      var alt = (product.title || pattern.name) + ' peel & stick wallpaper shown on a wall';
      scenes.innerHTML =
        '<div class="lp-hero__scene">' +
        '<img src="' +
        cdnImageUrl(heroPhoto, 1400) +
        '" srcset="' +
        cdnImageUrl(heroPhoto, 700) +
        ' 700w, ' +
        cdnImageUrl(heroPhoto, 1000) +
        ' 1000w, ' +
        cdnImageUrl(heroPhoto, 1400) +
        ' 1400w" sizes="(min-width: 900px) 54vw, 100vw" alt="' +
        escapeHtml(alt) +
        '" loading="eager" fetchpriority="high">' +
        '</div>';
    }

    if (ctaSet) {
      ctaSet.innerHTML =
        '<a class="lp-hero__cta lp-btn--dark" href="' +
        escapeHtml(pattern.url) +
        '">' +
        escapeHtml((data.ctaLabel || 'Shop') + ' ' + pattern.name) +
        '</a>';
    }

    if (nameSet) {
      nameSet.innerHTML = '<span class="lp-hero__name">' + escapeHtml(pattern.name) + '</span>';
    }
  }

  /* The product could not be used (unknown handle, no images): the hero keeps
     its first pattern, and the picker stays hidden because a product was
     asked for. lp-product-pending is left in place on purpose. */
  function markProductFallback() {
    document.documentElement.classList.add('lp-product-fallback');
  }

  function applyProductMode(product) {
    var pattern = buildPattern(product);
    if (!pattern) {
      markProductFallback();
      return null;
    }

    document.documentElement.classList.add('lp-product-ready');

    var hero = document.querySelector('[data-lp-hero]');
    if (hero) applyHeroProduct(hero, pattern, product);

    applySocialProof(product);
    applyFinalCta(pattern);
    bus.publish(pattern);

    return pattern;
  }

  var productHandle = getProductHandle();
  var productPromise = productHandle
    ? fetchProduct(productHandle).catch(function () {
        return null;
      })
    : Promise.resolve(null);

  /* ------------------------------------------------------------------- hero */

  /* The hero's pattern tile and lifestyle swap are handled by CSS (:checked
     rules and the tiles/scenes layers). This script rebuilds the hero in
     single-product mode and broadcasts the chosen pattern to the closing CTA
     and any other subscriber. */
  /* Sticky buy bar (phones): shown once the hero's buttons scroll out of
     view above, hidden again when they come back, and stood down while the
     closing CTA is on screen so it never covers a section that already has
     its own buttons. Follows the pattern bus. */
  function initSticky(root) {
    var sticky = document.querySelector('[data-lp-sticky]');
    if (!sticky || !('IntersectionObserver' in window)) return;

    var pastHero = false;
    var ctaInView = false;

    function render() {
      var show = pastHero && !ctaInView;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    var target = root.querySelector('.lp-hero__ctas') || root;
    new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        pastHero = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        render();
      },
      { threshold: 0 }
    ).observe(target);

    var cta = document.querySelector('[data-lp-cta]');
    if (cta) {
      new IntersectionObserver(
        function (entries) {
          ctaInView = entries[0].isIntersecting;
          render();
        },
        { threshold: 0, rootMargin: '0px 0px -72px 0px' }
      ).observe(cta);
    }

    var name = sticky.querySelector('[data-lp-sticky-name]');
    var link = sticky.querySelector('[data-lp-sticky-link]');
    bus.subscribe(function (pattern) {
      if (name && pattern.name) name.textContent = pattern.name;
      if (link && pattern.url) link.setAttribute('href', pattern.url);
    });
  }

  function initHero(root, productPattern, product) {
    if (!claim(root, 'lpHeroReady')) return;

    initSticky(root);

    if (productPattern && product) {
      /* boot() has usually applied product mode already; only rebuild a hero
         that hasn't been (a section re-rendered by the theme editor). */
      if (!root.classList.contains('lp-hero--product-mode')) {
        applyHeroProduct(root, productPattern, product);
      }
      bus.publish(productPattern);
      return;
    }

    var radios = Array.prototype.slice.call(root.querySelectorAll('[data-lp-chip]'));
    if (!radios.length) return;

    function publishPattern(radio) {
      var pattern = {
        img: radio.dataset.img || '',
        name: radio.dataset.name || '',
        url: radio.dataset.url || '',
        handle: radio.dataset.handle || '',
        variantId: radio.dataset.variantId || ''
      };
      retargetVisualizer(root, pattern);
      bus.publish(pattern);
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) publishPattern(radio);
      });
    });

    var checked = radios.filter(function (radio) {
      return radio.checked;
    })[0];
    publishPattern(checked || radios[0]);
  }

  /* ---------------------------------------------------------- before/after */

  function initBeforeAfter(root) {
    if (!claim(root, 'lpBaReady')) return;

    var range = root.querySelector('[data-lp-ba-range]');
    var paper = root.querySelector('[data-lp-ba-paper]');
    if (!range) return;

    function apply() {
      root.style.setProperty('--lp-ba-split', range.value + '%');
    }

    range.addEventListener('input', apply);
    apply();

    /* The wall keeps its editor-set pattern unless the section opts in to
       follow the hero's choice. */
    if (paper && root.hasAttribute('data-lp-ba-follow')) {
      bus.subscribe(function (pattern) {
        if (!pattern.img) return;
        /* A pattern from the hero is a flat swatch: repeat it, whatever mode
           the editor set for the static image. */
        root.classList.remove('lp-ba--photo');
        root.classList.add('lp-ba--tile');
        paper.style.backgroundImage = "url('" + pattern.img + "')";
      });
    }
  }

  /* -------------------------------------------------------------- final CTA */

  function initCta(root) {
    if (!claim(root, 'lpCtaReady')) return;

    var bg = root.querySelector('[data-lp-cta-bg]');
    if (!bg) return;

    bus.subscribe(function (pattern) {
      if (pattern.img) bg.style.backgroundImage = "url('" + pattern.img + "')";
    });
  }

  /* -------------------------------------------------------------------- FAQ */

  function initFaq(root) {
    if (!claim(root, 'lpFaqReady')) return;

    var items = Array.prototype.slice.call(root.querySelectorAll('[data-lp-faq-item]'));

    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        items.forEach(function (other) {
          if (other !== item) other.open = false;
        });
      });
    });
  }

  /* ------------------------------------------------------------------ wiring */

  var SECTIONS = [
    ['[data-lp-hero]', initHero],
    ['[data-lp-ba]', initBeforeAfter],
    ['[data-lp-cta]', initCta],
    ['[data-lp-faq]', initFaq]
  ];

  function init(scope, productPattern, product) {
    SECTIONS.forEach(function (entry) {
      var selector = entry[0];
      var fn = entry[1];

      if (selector === '[data-lp-hero]') {
        if (scope.matches && scope.matches(selector)) fn(scope, productPattern, product);
        Array.prototype.forEach.call(scope.querySelectorAll(selector), function (node) {
          fn(node, productPattern, product);
        });
        return;
      }

      if (scope.matches && scope.matches(selector)) fn(scope);
      Array.prototype.forEach.call(scope.querySelectorAll(selector), fn);
    });
  }

  function boot(scope) {
    productPromise.then(function (product) {
      var productPattern = null;

      if (product) {
        productPattern = applyProductMode(product);
        if (!productPattern) product = null;
      } else if (productHandle) {
        markProductFallback();
      }

      init(scope, productPattern, product);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  /* Theme editor: sections are replaced wholesale on every settings change. */
  document.addEventListener('shopify:section:load', function (event) {
    productPromise.then(function (product) {
      var productPattern = product ? buildPattern(product) : null;
      if (product && !productPattern) productPattern = null;
      init(event.target, productPattern, product);
    });
  });
})();
